// ---------------------------------------------------------------------------
// STATUS BADGE TONE
// ---------------------------------------------------------------------------
// The STATUS column of "Review Existing Goals" (all clients) and
// "Client History" (one client) colours each status so the therapist can read
// progress at a glance:
//
//   Achieved                          -> light green background, primary text
//   Not achieved by target date       -> pale red background, error-red text
//   In progress - delayed             -> #C23934 text on #FFEE80
//   In progress - on track            -> #BFEAF8 text on #0063A5
//
// Both tables previously rendered the status as several separate chips by
// splitting the label on "-", so "In progress - on track" became two uncoloured
// pieces ("In progress" / "on track"). A status is ONE value and has to be
// colour-coded as one, so the caller now renders a single badge whose modifier
// class comes from statusTone().
//
// The label itself is never rewritten here - what is stored is what is shown,
// so this module only decides which of the four tones applies. Comparison is
// normalised exactly like pgcNormaliseStatus() in therapist_api/services/
// goal_capacity.php (lowercase, punctuation and spacing removed), because rows
// written before the labels were reworded still carry the older spellings
// ("In Progress- on track", "in progress-on track"). Without that, an older row
// would silently fall back to the neutral style.
// ---------------------------------------------------------------------------

/** Shown when a goal has no status recorded at all - the same default the
 *  tables already used for an empty value. */
export const DEFAULT_STATUS = "In progress - on track";

/**
 * Canonical, punctuation-free form of a status label.
 *
 *   "In progress - on track" -> "inprogressontrack"
 *   "In Progress- on track"  -> "inprogressontrack"
 */
export const normaliseStatus = (value) =>
  String(value ?? "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

/**
 * Which of the five tones a status gets.
 *
 * @param {string} status stored status label, or "" when none is set
 * @returns {"achieved"|"not-achieved"|"delayed"|"on-track"} the tone to colour
 *          the badge with
 */
export const statusTone = (status) => {
  const key = normaliseStatus(status || DEFAULT_STATUS);

  // Order matters: "not achieved by target date" also contains "achieved", so
  // the finished-but-missed case has to be tested first. It gets its own tone -
  // while it shared the light green with Achieved, a therapist could not tell a
  // reached goal from a missed one at a glance.
  if (key.startsWith("notachieved")) return "not-achieved";
  if (key.startsWith("achieved")) return "achieved";
  if (key.includes("delayed")) return "delayed";
  // Anything else is a live goal on track - "In progress - on track" plus any
  // future "In progress - <something>" label, which is the optimistic reading.
  return "on-track";
};

/**
 * The label to display for a status, with the blank case filled in.
 */
export const statusLabel = (status) => {
  const label = String(status ?? "").trim();
  return label === "" ? DEFAULT_STATUS : label;
};

// ---------------------------------------------------------------------------
// OUTCOME FOLDERS
// ---------------------------------------------------------------------------
// Both history tables ("Review Existing Goals" across all clients, "Client
// History" for one client) group their finished rows into two folders so the
// outcome of a goal journey can be read without scanning the whole table:
//
//   Achieved                     -> "achieved"
//   Not achieved by target date  -> "not-achieved"
//
// Anything else (a goal still in progress) belongs to neither folder.
//
// This is deliberately SEPARATE from statusTone() above. The badge colours
// Achieved and Not achieved by target date differently, and the folders have to
// agree with that split rather than re-derive it - but they answer a different
// question (which group does this row belong to) rather than a styling one, and
// the folders keep working even if the badge colours are retuned.
// ---------------------------------------------------------------------------

/** The two folders, in the order they are shown. */
export const OUTCOME_FOLDERS = [
  { id: "achieved", label: "Achieved" },
  { id: "not-achieved", label: "Not Achieved " },
];

/**
 * Which outcome folder a status belongs to, if any.
 *
 * @param {string} status stored status label
 * @returns {"achieved"|"not-achieved"|null} null while the goal is still live
 */
export const statusOutcome = (status) => {
  const key = normaliseStatus(status || DEFAULT_STATUS);

  // "Not achieved by target date" also CONTAINS "achieved", so the missed case
  // has to be recognised before the reached one or every missed goal would be
  // filed under the wrong folder.
  if (key.startsWith("notachieved")) return "not-achieved";
  if (key.startsWith("achieved")) return "achieved";
  return null;
};

export default statusTone;
